import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMap } from './MapContext';
import { isThreeDAvailable } from '../config/environment';
import { Logger } from '../services/Logger';
import type { Track } from '../services/track-parsers/track-types';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface ThreeDState {
  /** User-facing toggle. Flipping to true starts the lazy Cesium import. */
  enabled: boolean;
  /** True while the cesium chunk / viewer is being initialised. */
  loading: boolean;
  /** Populated once the viewer is ready. */
  ready: boolean;
  /** Reason why 3D is unavailable (missing tokens, load error, etc.). */
  unavailableReason: string | null;
}

interface ThreeDContextValue extends ThreeDState {
  setEnabled: (v: boolean) => void;
  /** Returns an unmount function. Safe to call multiple times. */
  attachContainer: (container: HTMLElement | null) => void;
  /** Render a flight track in the 3D view. Returns an unmount function. */
  showFlightPath: (track: Track) => Promise<() => void>;
  /** Move the aircraft billboard for the active track to a 0..1 fraction. */
  setAircraftAtFraction: (trackId: string, t: number) => void;
}

const ThreeDContext = createContext<ThreeDContextValue | null>(null);

export function useThreeD(): ThreeDContextValue {
  const ctx = useContext(ThreeDContext);
  if (!ctx) throw new Error('useThreeD must be used within ThreeDProvider');
  return ctx;
}

export const ThreeDProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { map } = useMap();
  const [state, setState] = useState<ThreeDState>(() => ({
    enabled: false,
    loading: false,
    ready: false,
    unavailableReason: isThreeDAvailable() ? null : 'VITE_CESIUM_ION_TOKEN / VITE_GOOGLE_MAPS_KEY not set',
  }));

  const containerRef = useRef<HTMLElement | null>(null);
  const viewerHandleRef = useRef<{ viewer: any; destroy: () => void } | null>(null);
  const syncHandleRef = useRef<{ destroy: () => void } | null>(null);
  const pathHandlesRef = useRef<Map<string, { destroy: () => void; setAircraftAtFraction: (t: number) => void }>>(new Map());

  const setEnabled = useCallback((v: boolean) => {
    setState((prev) => ({ ...prev, enabled: v }));
  }, []);

  const attachContainer = useCallback((container: HTMLElement | null) => {
    containerRef.current = container;
  }, []);

  // Life-cycle: when enabled flips true AND we have a container, build the viewer.
  useEffect(() => {
    if (!state.enabled || state.ready || state.loading) return;
    if (!containerRef.current || !map) return;
    if (!isThreeDAvailable()) {
      setState((s) => ({ ...s, unavailableReason: 'Missing 3D API keys' }));
      return;
    }

    let cancelled = false;
    setState((s) => ({ ...s, loading: true, unavailableReason: null }));

    (async () => {
      try {
        const [{ createCesium3DView }, { createCameraSync }] = await Promise.all([
          import('./Cesium3DView'),
          import('./CameraSyncService'),
        ]);
        if (cancelled) return;

        const handle = await createCesium3DView(containerRef.current!);
        if (!handle) throw new Error('Cesium Ion token missing');
        viewerHandleRef.current = handle;

        const sync = await createCameraSync(map, handle.viewer as any);
        syncHandleRef.current = sync;

        // Push current MapLibre viewport once so Cesium starts pointing at
        // the same place.
        sync.pushFromMapLibre();

        if (!cancelled) {
          setState((s) => ({ ...s, loading: false, ready: true }));
        }
      } catch (e) {
        Logger.error('3D view initialisation failed', { error: (e as Error).message });
        if (!cancelled) {
          setState((s) => ({
            ...s,
            loading: false,
            ready: false,
            unavailableReason: (e as Error).message,
          }));
        }
      }
    })();

    return () => { cancelled = true; };
  }, [state.enabled, state.ready, state.loading, map]);

  // Life-cycle: teardown when disabled.
  useEffect(() => {
    if (state.enabled) return;
    // tear down
    for (const h of pathHandlesRef.current.values()) {
      try { h.destroy(); } catch { /* ok */ }
    }
    pathHandlesRef.current.clear();
    syncHandleRef.current?.destroy();
    syncHandleRef.current = null;
    viewerHandleRef.current?.destroy();
    viewerHandleRef.current = null;
    setState((s) => (s.ready ? { ...s, ready: false } : s));
  }, [state.enabled]);

  const showFlightPath = useCallback(async (track: Track): Promise<() => void> => {
    const handle = viewerHandleRef.current;
    if (!handle) return () => { /* noop */ };
    // Remove any existing path for this track first.
    const existing = pathHandlesRef.current.get(track.id);
    if (existing) { try { existing.destroy(); } catch { /* ok */ } }

    const { addFlightPathToCesium } = await import('./CesiumFlightPath');
    const pathHandle = await addFlightPathToCesium(handle.viewer, track);
    pathHandlesRef.current.set(track.id, pathHandle);

    return () => {
      try { pathHandle.destroy(); } catch { /* ok */ }
      pathHandlesRef.current.delete(track.id);
    };
  }, []);

  const setAircraftAtFraction = useCallback((trackId: string, t: number) => {
    const h = pathHandlesRef.current.get(trackId);
    if (h) h.setAircraftAtFraction(t);
  }, []);

  const value = useMemo<ThreeDContextValue>(() => ({
    ...state,
    setEnabled,
    attachContainer,
    showFlightPath,
    setAircraftAtFraction,
  }), [state, setEnabled, attachContainer, showFlightPath, setAircraftAtFraction]);

  return <ThreeDContext.Provider value={value}>{children}</ThreeDContext.Provider>;
};
